import { html, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { customElement } from '../base-element';
import { PopoverBaseWC } from '../popover/src/popover-base.wc';
import styles from './popover-tooltip.scss?inline';

@customElement('ps-popover-tooltip')
export class PopoverTooltipWC extends PopoverBaseWC {
  static styles = unsafeCSS(styles);

  /**
   * Popover size
   */
  @property({ reflect: true }) size: 'none' | 'xsmall' | 'small' | 'large' =
    'large';

  constructor() {
    super();
    this.distance = 12;
    this.skidding = 8;
    this.hasArrow = true;

    this.triggerAction = 'click-and-hover';
    this.closeOnClickOutside = false;
    this.disableFocusTrap = true;
  }

  /**
   * True when the tooltip has non-whitespace slotted content to display.
   * Guards against showing an empty bubble (PSTUDIO-3335): the per-cell error
   * tooltip in Pro Studio is mounted unconditionally (PSTUDIO-2321) and its
   * `click-and-hover` trigger calls `show()` on mouseover regardless of whether
   * there is an error message, so an empty cell would otherwise pop a blank
   * bubble.
   *
   * Read at evaluation time, and NOT reactive: the `--shown` gate is re-checked
   * only when a reactive property changes or an update is requested. Content and
   * `active` do NOT always arrive together — the consumer projects its text in a
   * React commit while `show()` comes from a `mouseover`, which is exactly the
   * race PSTUDIO-4034 was filed for. {@link deferShowUntilContentArrives} covers
   * the refused-show case and requests an update itself.
   */
  private get hasContent(): boolean {
    return (this.textContent ?? '').trim().length > 0;
  }

  /**
   * Watches for the slotted message to arrive after a `show()` was refused over
   * an empty slot. Attached only while such a show is pending — every trigger
   * path funnels through `show()`, so a tooltip nobody is interacting with never
   * allocates one. The exception is `defaultActive`, which calls `show()` from
   * `firstUpdated()`: such a tooltip keeps one observer until its content lands
   * or it is removed.
   */
  private pendingShowObserver?: MutationObserver;

  /**
   * Never open an empty tooltip. Every trigger path (hover, click, focus,
   * keyboard, defaultActive) funnels through `show()`, so a single guard here
   * covers them all.
   *
   * A refusal is REMEMBERED rather than dropped (PSTUDIO-4034). `triggerAction`
   * is `click-and-hover`, so a hover opens the tooltip from a single `mouseover`
   * — and the browser emits no further `mouseover` while the pointer rests on
   * the trigger. A hover landing in the frame before the consumer projects its
   * message would otherwise be the only chance to open, leaving the tooltip shut
   * until the pointer left and came back.
   */
  show(): void {
    if (!this.hasContent) {
      this.deferShowUntilContentArrives();
      return;
    }
    this.stopWaitingForContent();
    super.show();
  }

  /**
   * Cancels a pending deferred show: once the pointer has left (or any other
   * close path ran), a message arriving later must NOT pop a bubble the user is
   * no longer pointing at.
   */
  hide(e?: Event, reason = 'unknown'): void {
    this.stopWaitingForContent();
    super.hide(e, reason);
  }

  disconnectedCallback(): void {
    this.stopWaitingForContent();
    super.disconnectedCallback();
  }

  /**
   * The consumer drives `active` as a reactive prop (`CellErrorTooltip` renders
   * `active={Boolean(errorMessage) && …}`), so closing it that way never calls
   * `hide()`. Two things must still happen on the way down: a pending deferral
   * is cancelled, and the trigger's `data-popover-open` marker — which
   * `super.show()` set from the deferred replay — is cleared. The marker is not
   * cosmetic: `draggable-row.scss` paints the row from it, and an e2e guard
   * asserts it is gone.
   */
  async updated(changedProps: Map<string, unknown>): Promise<void> {
    if (changedProps.has('active') && !this.active) {
      this.stopWaitingForContent();
      this.triggerEl?.removeAttribute('data-popover-open');
    }
    await super.updated(changedProps);
  }

  /**
   * Re-runs the refused `show()` as soon as the slot carries text.
   *
   * Watches `characterData` as well as `childList`: the sole consumer renders
   * `{errorMessage ?? ''}`, so React may either insert a text node or write into
   * one it already rendered — a `slotchange` listener would only ever see the
   * former.
   */
  private deferShowUntilContentArrives(): void {
    // One observer per element, replaced by nothing until it fires or is
    // cancelled. `mouseover` BUBBLES from the trigger's descendants and
    // `onMouseOver` only returns early once `active` is true — which a refused
    // show never sets — so without this guard every pointer move inside the
    // trigger would allocate another observer and orphan the previous one, and
    // an orphan outlives `hide()`.
    if (this.pendingShowObserver) return;

    this.pendingShowObserver = new MutationObserver(() => {
      if (!this.hasContent) return;
      if (!this.isStillEngaged()) return;
      this.stopWaitingForContent();
      super.show();
      // `hasContent` is not reactive and `active` may already be true (the
      // consumer drives it as a prop), in which case `super.show()` changes no
      // reactive property and Lit would never re-render the `--shown` gate.
      this.requestUpdate();
    });
    this.pendingShowObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  private stopWaitingForContent(): void {
    this.pendingShowObserver?.disconnect();
    this.pendingShowObserver = undefined;
  }

  /**
   * Is the reason the tooltip was asked to open still true?
   *
   * A deferred show must not rely on someone having called `hide()`, because
   * several close paths in the base class never reach it: `onMouseOut` returns
   * early while the trigger is focused, Escape is only listened for once the
   * popover is already active, and a consumer that drives `active` as a prop
   * bypasses `hide()` altogether. Re-checking the condition at replay time
   * covers all of them at once, and keeps the bubble from appearing over a
   * trigger the pointer has already left.
   *
   * With no resolved trigger there is nothing to interrogate (the case in unit
   * tests, and for a `defaultActive` tooltip), so the show goes ahead.
   */
  private isStillEngaged(): boolean {
    const trigger = this.triggerEl;
    if (!trigger) return true;
    return trigger.matches(':hover') || trigger.matches(':focus-within');
  }

  render() {
    return html`
      <div
        id="popover-container"
        class=${classMap({
          'c-popover-tooltip': true,
          'c-popover-tooltip--shown': this.active && this.hasContent,
        })}
        style="transform: ${this.transform}"
      >
        <slot class="c-popover-tooltip__body"></slot>

        <div class="c-popover-tooltip__arrow" id="popover-arrow"></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-popover-tooltip': PopoverTooltipWC;
  }
  enum PSElementTagNameMap {
    'ps-popover-tooltip' = 'ps-ppopover-tooltip',
  }
}
